/**
 * Transforms the data returned by the data provider (ie.: Fast Simon or storefront API) to Shopify liquid format.
 *
 * @param {Object} data - The data returned by the data provider
 * @param {String} dataProvider - The data provider used to fetch the data
 * @returns {Object} The data in Shopify liquid format
 */
export const dataProvider = (data, dataProvider) => {
  switch (dataProvider) {
    case 'fast-simon':
      return data.map((product) => {
        return fastSimonToShopifyProduct(product);
      });
    case 'storefront-api':
      return data.map((product) => {
        return storefrontAPIToShopifyProduct(product);
      });
    case 'rebuy-api':
      // TODO: Implement rebuy API to Shopify liquid format, seems mostly 1 to 1 at a glance
      return data.map((product) => {
        return product;
      });
  }
};

export const storefrontAPIToShopifyProduct = (product) => {
  const firstAvailableVariant = product.variants.edges.find((variant) => variant.node.availableForSale)?.node || product.variants.edges[0].node;

  const newProduct = {
    id: product.id.replace('gid://shopify/Product/', ''),
    title: product.title,
    handle: product.handle,
    description: product.descriptionHtml,
    published_at: product.publishedAt,
    vendor: product.vendor,
    type: product.productType,
    tags: product.tags,
    price: product.priceRange.minVariantPrice.amount * 100,
    price_min: product.priceRange.minVariantPrice.amount * 100,
    price_max: product.priceRange.maxVariantPrice.amount * 100,
    available: product.availableForSale,
    price_varies: product.priceRange.minVariantPrice.amount !== product.priceRange.maxVariantPrice.amount,
    compare_at_price: product.compareAtPriceRange?.minVariantPrice.amount * 100,
    compare_at_price_min: product.compareAtPriceRange?.minVariantPrice.amount * 100,
    compare_at_price_max: product.compareAtPriceRange?.maxVariantPrice.amount * 100,
    compare_at_price_varies: product.compareAtPriceRange?.minVariantPrice.amount !== product.compareAtPriceRange.maxVariantPrice.amount,
    options_with_values: product.options.map((option, index) => (
      {
        name: option.name,
        position: index + 1,
        values: option.values,
        selected_value: null
      }
    )),
    variants: product.variants.edges.map((variant) => storefrontAPIVariantToShopifyVariant(variant.node)),
    images: null, // keeping null since we don't need images for product cards (variant images are enough)
    featured_image: product.featuredImage?.url,
    featured_media: null, // keeping null since we only show images for product cards and not videos/models
    options: product.options.map((option) => option.name),
    selected_or_first_available_variant: storefrontAPIVariantToShopifyVariant(firstAvailableVariant),
    media: null, // keeping null since we only show images for product cards and not videos/models
    content: product.description,
    url: product.onlineStoreUrl
  }

  return newProduct;
}

const storefrontAPIVariantToShopifyVariant = (variant) => {
  return {
    id: variant.id.replace('gid://shopify/ProductVariant/', ''),
    title: variant.title,
    available: variant.availableForSale,
    barcode: variant.barcode,
    compare_at_price: variant.compareAtPrice?.amount * 100,
    image: variant.image?.url,
    price: variant.price.amount * 100,
    requires_shipping: variant.requiresShipping,
    selected_options: variant.selectedOptions,
    sku: variant.sku,
    weight: variant.weight,
    weight_unit: variant.weightUnit,
    option1: variant.selectedOptions[0].value,
    option2: variant.selectedOptions[1]?.value,
    option3: variant.selectedOptions[2]?.value
  }
}

export const fastSimonToShopifyProduct = (product) => {
  const {
    vra, // variants
    v_c, // variants count
    att, // attributes
    id, // id
    iso, // product sold out
    p_c, // price compare
    p_max_c, // price compare max
    p_min_c, // price compare min
    d, // description
    t, // image
    t2, // fall back image
    u, // url
    l, // title
    p, // price
    p_max, // product variant max price range
    p_min, // product variant min price range
    b, // product/variant on sale
    s // idk tbh, product search query id? needed for autocomplete events reporting
  } = product;

  // Product attributes
  const productAttributes = Object.fromEntries(att || []);
  const categories = (productAttributes["Categories"] || []).flat();
  const tags = productAttributes["Tag"] || [];
  const vendor = (productAttributes["Vendor"] || [])[0] || "";

  // Product options name
  const productAttributesEntris = Object.entries(productAttributes);
  const optionsName = productAttributesEntris
    .filter(([key]) => key.startsWith("Option"))
    .map(([, value]) => value)
    .flat();

  // Product options with values
  const optionsWithValues = productAttributesEntris
    .filter(([key]) => key.startsWith("Option"))
    .map(([name, values]) => {
      const attributeName = values[0];
      const position = Number(name.replace(/\D/g, ''));
      const optionValues = vra
        .map(([, attributes]) => attributes.find(([attrName]) => attrName === attributeName))
        .filter(Boolean)
        .flatMap(([, values]) => values);
      return ({
        name: attributeName,
        position: position,
        values: [...new Set(optionValues)]
      })
    });

  // Variant objects
  const shopifyVariants = vra ? vra.map(([variantId, properties]) => {
    const variantProperties = Object.fromEntries(properties.map(([name, values]) => [name, Array.isArray(values) ? values[0] : values]));
    const variantImage = variantProperties["imgs"];
    const variantPrice = variantProperties["Price"];
    const variantCompareAtPrice = variantProperties["Compare at price"];

    const variantTitle = () => {
      if (vra.length === 1) {
        return l;
      }

      let optionValues = [];

      for (let i = 0; i < 3; i++) {
        const attributeOptionName = `Option ${i + 1} name`;

        if (productAttributes[attributeOptionName]) {
          const optionName = productAttributes[attributeOptionName];
          const optionValue = variantProperties[optionName];

          if (optionValue) {
            optionValues.push(optionValue);
          }
        }
      }

      return optionValues.join(" / ");
    }

    let variant = {
      id: parseInt(variantId),
      title: variantTitle(),
      name: variantTitle(),
      available: variantProperties["Sellable"],
      compare_at_price: variantCompareAtPrice ? variantCompareAtPrice.split(":")[1] * 100 : null,
      featured_image: variantImage || null,
      featured_media: variantImage || null,
      price: variantPrice ? variantPrice.split(":")[1] * 100 : null,
      product: null, // Will be updated after ShopifyProduct creation
      sku: variantProperties["Product-sku"] || "",
      url: `${u}?variant=${variantId}`,
      weight: parseFloat(variantProperties["Weight"]) || 0,
      option_color: variantProperties["Colour"] || "",
      metafields: {
        custom: {
          shipping_group: variantProperties["Custom.shipping group"] || ""
        },
        sage: {
          AllowItemInWebStore: variantProperties["Sage.AllowItemInWebStore"] || ""
        }
      }
    };

    // Update variant with options value
    optionsWithValues.forEach(option => {
      variant[`option${option.position}`] = variantProperties[option.name] || null;
    });

    return variant;
  }) : [];

  // Product first available variant
  const firstAvailableIndex = shopifyVariants.findIndex(variant => variant.available);
  const firstAvailableVariant = firstAvailableIndex !== -1 ? shopifyVariants[firstAvailableIndex] : null;

  // metafields
  const metafields = {
    content: {
      promotions: productAttributes["content.promotions"] || null,
      short_description: productAttributes["content.short_description"] || null,
    }
  }

  // color swatches
  const colours = shopifyVariants.map(variant => {
    const colour = variant.option_color;
    const image = variant.featured_image || null;

    if (!colour) {
      return null;
    }

    return {
      id: variant.id,
      colour: colour,
      image: image,
      variant: variant,
    }
  })
    .filter(Boolean) // Remove null values
    .filter((colour) => colour.variant.available) // Filter out unavailable variants
    .filter((colour) => { // filter out variants where the price ends in .97
      const priceWithNoDecimals = colour.variant.price % 1;
      const priceString = priceWithNoDecimals.toString();
      const priceDecimal = priceString.slice(-2);

      return priceDecimal !== "97";
    })
    .filter((colour) => colour.image !== null); // Filter out colours without images
  // Remove duplicate colours
  const uniqueColours = Array.from(new Set(colours.map(item => item.colour)))
    .map(colour => {
      return colours.find(item => item.colour === colour);
    });
  // Remove null values
  const filteredColours = uniqueColours.filter(item => item !== null);

  // Product object
  const shopifyProduct = {
    id: parseInt(id),
    available: !iso,
    collections: categories,
    compare_at_price: p_c ? p_c * 100 : null,
    compare_at_price_max: p_max_c ? p_max_c * 100 : null,
    compare_at_price_min: p_min_c ? p_min_c * 100 : null,
    compare_at_price_varies: p_max_c !== p_min_c,
    description: d,
    featured_image: t || t2 || null,
    first_available_variant: firstAvailableVariant,
    handle: u.split("/products/")[1].split("?")[0],
    has_only_default_variant: (vra && vra.length === 1) || (v_c === 1),
    images: [
      ...(productAttributes?.["Product images"] || [])
    ],
    options: optionsName,
    options_with_values: optionsWithValues,
    price: p * 100,
    price_max: p_max ? p_max * 100 : null,
    price_min: p_min ? p_min * 100 : null,
    price_varies: Number(p_max) > 0 ? Number(p_min) !== Number(p_max) : false,
    selected_or_first_available_variant: firstAvailableVariant || shopifyVariants[0],
    selected_variant: firstAvailableVariant || shopifyVariants[0],
    tags: tags,
    title: l,
    url: u,
    vendor: vendor,
    variants: shopifyVariants,
    variants_count: v_c,
    on_sale: b,
    s: s,
    metafields: metafields,
    colours: filteredColours.length > 0 ? filteredColours : null,
  };

  // Update product reference in variants
  shopifyProduct.variants.forEach(variant => {
    variant.product = shopifyProduct;
  });

  return shopifyProduct;
};
